import type { PaymentTaxesTranslations } from "../interfaces/paymentTaxes";

export const paymentTaxesTranslations: PaymentTaxesTranslations = {
  // Page
  page_title: "Taxes",
  page_subtitle:
    "IVA, 4x1000 or any tax per country, corridor by corridor: on the commission or on the amount, who pays it and which organizations pay differently",
  refresh_button: "Refresh",
  refresh_aria: "Refresh taxes",
  new_tax_button: "New tax",
  why_banner:
    "Taxes the client pays are added to what they are charged, like the commission; taxes Coloca absorbs leave their amounts unchanged. Quotes and batches already validated keep the tax they were priced with.",
  truncated_notice: "There are more versions than loaded; what is shown may be incomplete.",
  error_load: "We could not load the taxes",
  error_load_detail: "Check your connection and try again.",
  error_retry: "Try again",
  empty_title: "No taxes yet",
  empty_description: "Create the first one, for example VAT on the commission or the 4x1000 on the amount.",
  empty_org_title: "No taxes for this organization",
  empty_org_description: "No general or own tax applies to this organization.",

  // Scope selector
  scope_label: "Scope",
  scope_placeholder: "Search by name, email or ID…",
  scope_loading: "Loading organizations…",
  scope_empty: "No organizations found.",
  scope_searching: "Searching organizations…",
  scope_search_error: "We could not complete the search; showing the organizations already loaded.",
  scope_global_option: "General (every organization)",
  scope_global_hint: "General taxes: what every organization without its own rate pays.",
  scope_org_hint:
    "What this organization pays. Changes here create its own rate, which wins over the general one.",
  scope_catalog_error:
    "We could not load the organizations catalog; some may show their ID instead of the name.",
  scope_catalog_truncated: "There are more organizations than listed; type to search them all.",

  // Rows
  base_commission: "On the commission",
  base_gross: "On the amount",
  bearer_client: "Paid by the client",
  bearer_coloca: "Absorbed by Coloca",
  bearer_client_hint: "Added to what the client pays, just like the commission.",
  bearer_coloca_hint: "The client sees no change; Coloca books it as an expense.",
  where_all: "Collections and payouts",
  where_payin: "Collections only",
  where_payout: "Payouts only",
  where_any_rail: "every rail",
  exempt: "Exempt",
  source_general: "General",
  source_own: "Own",
  inherited_from: "From its own rate for {{where}}",
  exceptions_count: "Exceptions: {{count}}",
  scheduled: "Scheduled change",
  since: "Since {{date}}",
  example_commission: "Commission {{amount}}",
  example_gross: "Amount {{amount}}",
  example_tax_fallback: "Tax",
  actions_aria: "Actions for {{name}}",
  action_change: "Change rate",
  action_change_special: "Change own rate",
  action_special: "Give own rate",
  action_special_here: "Own rate for this row only",
  action_exempt: "Exempt",
  action_back_general: "Back to the general rate",
  action_history: "View history",

  // Dialog
  dialog_title_new: "New tax",
  dialog_title_change: "Change {{name}}",
  dialog_title_special: "Own rate of {{name}}",
  dialog_scope_general: "Applies to every organization without its own rate.",
  dialog_scope_org: "Only for {{org}}.",
  field_country: "Country",
  field_code: "Code",
  field_code_hint: "2 to 8 letters or digits, for example IVA or GMF. Groups the versions of the tax.",
  field_name: "Name",
  field_name_hint: "What clients and operations read, for example IVA or 4x1000.",
  field_base: "Charged",
  field_base_locked: "A tax's base does not change between versions.",
  field_rate: "Rate",
  field_rate_hint: "Percentage of Coloca's commission. 0 exempts the scope.",
  field_rate_gross_hint:
    "Percentage of the amount: what the payer sends on a collection, what the person receives on a payout. At most 10 %.",
  field_bearer: "Who pays it",
  field_direction: "Applies to",
  field_rail: "Rail",
  field_rail_any: "Any rail",
  eff_now_label: "In force from now",
  eff_date_label: "From",
  eff_time_label: "Time",
  reason_label: "Reason",
  reason_placeholder: "For example: tax authority ruling, agreement with the client…",
  reason_hint: "Internal: kept in the history and in the change alert.",
  review_title: "Review the change",
  review_before: "Today",
  review_after: "With the change",
  review_none: "No tax",
  review_affects:
    "Affects every organization without its own {{name}} rate in {{country}}, from the date set.",
  review_ack: "I understand this changes what clients pay from that date.",
  button_cancel: "Cancel",
  button_back: "Back",
  button_review: "Review",
  button_save: "Save",
  validation_code: "Use 2 to 8 letters or digits, no spaces.",
  validation_name: "Write a name of up to 64 characters.",
  validation_rate: "Write a percentage between 0 and 100, with up to 4 decimals.",
  validation_rate_gross: "On the whole amount the maximum is 10%.",
  validation_valid_from: "Pick the day it starts.",
  validation_reason: "Write the reason for the change.",
  validation_ack: "Confirm you reviewed the change.",

  // Exemptions
  exempt_title: "Exempt from {{name}}",
  exempt_description:
    "{{org}} stops paying {{name}} on {{where}} from now. Past operations and quotes in force do not change.",
  exempt_confirm: "Exempt",
  back_general_title: "Back to the general {{name}} rate",
  back_general_description:
    "{{org}}'s own versions of {{name}} for {{where}} are deleted; it pays the general rate from now.",
  back_general_confirm: "Back to the general rate",
  back_general_affects: "This rule also applies to: {{rows}}. All of them go back to the general rate.",

  // History
  history_title: "{{name}} history",
  history_description: "Every version of this scope, newest first.",
  history_scope_general: "General rate · {{where}}. Every organization without its own rate pays it.",
  history_scope_own: "{{org}}'s own rate · {{where}}.",
  history_scope_covers: "This rule applies to: {{rows}}. Deleting a version changes all of them.",
  history_read_only_general: "Read only: the general rate is corrected with no organization picked.",
  history_read_only_inherited: "Read only: this rule is corrected from its own row.",
  history_version: "Version {{version}}",
  history_in_force: "In force",
  history_scheduled: "Scheduled",
  history_superseded: "Replaced",
  history_reason_none: "No reason",
  history_delete: "Correct: delete this version",
  history_delete_description:
    "The previous version rules again. To stop charging the tax, save a version at 0%.",
  history_delete_confirm: "Delete version",

  // Feedback
  toast_saved: "{{name}} saved",
  toast_deleted: "Version deleted",
  toast_exempted: "{{org}} is now exempt from {{name}}",
  toast_back_general: "{{org}} is back on the general {{name}} rate",
  error_save: "We could not save the tax.",
  error_delete: "We could not delete the version.",
  error_no_write_permission: "You do not have permission to change taxes.",

  // Corridors view
  tab_corridors: "Corridors",
  tab_history: "Change history",
  tab_rules: "Rules",
  tab_intro_corridors:
    "Every corridor with the taxes it charges today, worked on the example amount. Edit them from its row.",
  tab_intro_history: "Every change to a tax, newest first: who made it, what it replaced and why.",
  tab_intro_rules:
    "Each country's rules with their versions. A new tax is created here, and a version corrected.",
  guide_title: "How taxes work",
  guide_base_title: "What it is worked on",
  guide_base_body:
    "On Coloca's commission (IVA) or on the amount (the 4x1000). On a payout the amount is what the person receives; on a collection, what the payer sends.",
  guide_bearer_title: "Who pays it",
  guide_bearer_body:
    "The client, added to what they are charged like the commission; or Coloca, and the client sees no change.",
  guide_scope_title: "Where it applies",
  guide_scope_body:
    "Always in the corridor's country. A rule can cover the whole country or only one rail or direction; the most specific wins.",
  guide_own_title: "Own rate",
  guide_own_body:
    "Pick an organization above to see what it pays. Its own rate beats the general one; at 0 % it is exempt.",
  guide_changes_title: "Changes",
  guide_changes_body:
    "Every change is a new dated version; nothing is rewritten. Quotes in force keep the tax they were priced with.",
  guide_untaxed_title: "What is not taxed",
  guide_untaxed_body:
    "USDT operations never carry taxes. A corridor with no taxes charges the commission only.",
  banner_general_title: "General taxes",
  banner_general_body:
    "Every organization without its own rate pays them. What you change here applies to all of them.",
  banner_general_orgs: "Organizations with their own rate on some corridor: {{count}}",
  banner_org_title: "Taxes of {{name}}",
  banner_org_body: "What {{name}} pays. What you change here applies to {{name}} only.",
  banner_back_to_general: "See the general ones",
  kpi_all_label: "Corridors",
  kpi_all_meta: "{{taxed}} with taxes",
  kpi_unavailable: "No corridor catalog",
  kpi_taxed_label: "With taxes",
  kpi_taxed_meta: "Charge at least one tax",
  kpi_own_label: "With an own rate",
  kpi_own_meta_general: "Some organization pays differently",
  kpi_own_meta_org: "Where this organization pays differently",
  kpi_scheduled_label: "Scheduled changes",
  kpi_scheduled_meta: "A version starts later on",
  corridors_th_corridor: "Corridor",
  corridors_th_taxes_general: "General taxes",
  corridors_th_taxes_org: "This organization's taxes",
  corridors_th_own: "Own rate",
  corridors_th_client: "Client pays",
  corridors_th_coloca: "Coloca absorbs",
  routes_error: "We could not load the corridors",
  routes_error_detail: "Without the corridor catalog we cannot show which taxes each one charges.",
  corridors_empty_title: "No corridors",
  corridors_empty_description: "A corridor shows up here with its taxes once it has a route.",
  corridors_filtered_empty_title: "No corridor matches",
  corridors_filtered_empty_description: "Pick another tile above to see more corridors.",
  taxes_none: "No taxes",
  commission_unknown: "no commission set",
  source_general_baseline: "General: {{rate}}",
  source_no_general: "No general tax",
  own_none: "None",
  own_count: "Organizations: {{count}}",
  own_popover_title: "Organizations with their own rate",
  own_popover_hint: "Pick one to see and edit what it pays.",
  action_edit_taxes: "Edit taxes",
  action_view_rules: "See rules",
  action_more_aria: "More actions for {{corridor}}",
  edit_title_general: "Edit taxes",
  edit_title_own: "{{name}}'s own rate",
  edit_applies_general: "Applies to every organization without its own rate",
  edit_applies_own: "Applies to {{name}} only",
  edit_no_taxes_title: "{{country}} has no taxes",
  edit_no_taxes_description:
    "Create the tax first, for example IVA on the commission; it is then edited from each corridor.",
  edit_tax_label: "Tax",
  edit_target_label: "Where the change is saved",
  edit_target_broad: "On the rule this corridor follows: {{where}} (corridors: {{count}})",
  edit_target_corridor: "On this corridor only",
  edit_exempt: "Exempt (0 %)",
  edit_valid_from: "In force from {{date}}",
  edit_collision: "A version of this rule already starts at that very moment. Pick another date or time.",
  edit_compare_title: "Today vs. with the change",
  edit_compare_amount: "On {{amount}}",
  review_intro: "Corridors that change: {{count}}. Figures on each one’s example amount.",
  review_orgs_kept: "These organizations have their own rate and do not change ({{count}}):",
  review_orgs_none: "No organization has its own rate on these corridors.",
  remove_title: "Back to the general taxes",
  remove_description: "{{name}} drops its own rate on {{corridor}} and pays the general taxes from now.",
  remove_broad_note:
    "This own rate covers several corridors ({{count}}) and all of them go back to the general one:",
  remove_after_title: "After the change",
  remove_after_none: "No tax",
  remove_versions_title: "Versions deleted",
  history_drawer_title: "History of {{corridor}}",
  history_drawer_desc_general: "Changes to this corridor's general taxes.",
  history_drawer_desc_org: "The changes that reach {{name}} on this corridor: general and own.",
  history_empty: "No changes yet",
  history_deleted_note: "Versions deleted as a correction do not show here.",
  history_role_own: "{{name}}'s own",
  history_unknown_author: "unknown author",
  history_new: "New",
  period_starts: "Starts on {{from}}",
  period_open: "Since {{from}}",
  period_range: "{{from}} → {{to}}",
  feed_today: "Today",
  feed_yesterday: "Yesterday",
  feed_filter_source: "Rule type",
  feed_source_all: "All",
  feed_source_general: "General",
  feed_source_own: "Own",
  feed_count: "Changes: {{count}}",
  feed_empty_title: "No changes",
  feed_empty_description: "Creating or changing a tax shows up here.",
  feed_load_more: "Show more",
  toast_scheduled: "{{name}} saved; in force from {{date}}",
  toast_removed: "{{name}} is back on the general taxes",
};
